<script setup lang="ts">
import { onMounted } from "vue";

// Admin app base URL — "Get started" CTAs send prospects to the organizer
// sign-up. Falls back to the production admin domain if the env isn't set.
const ADMIN_URL =
  (import.meta.env.VITE_ADMIN_URL as string | undefined) ||
  "https://admin.convena.my.id";
const registerUrl = `${ADMIN_URL.replace(/\/$/, "")}/register`;
const loginUrl = `${ADMIN_URL.replace(/\/$/, "")}/login`;

const features = [
  {
    title: "Formulir registrasi kustom",
    desc: "Buat formulir pendaftaran sesuai kebutuhan tiap acara — pertanyaan, paket, dan kuota Anda sendiri.",
    path: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  },
  {
    title: "Transfer bank + kode unik",
    desc: "Rekonsiliasi pembayaran manual otomatis lewat kode unik di nominal transfer — tanpa cek mutasi satu per satu.",
    path: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
  },
  {
    title: "Tiket & notifikasi via WhatsApp",
    desc: "Kirim tiket digital dan pengingat langsung ke WhatsApp peserta — saluran yang benar-benar mereka buka.",
    path: "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 3v-3z",
  },
  {
    title: "Check-in QR di lokasi",
    desc: "Scan QR peserta saat hari-H untuk check-in cepat dan akurat, tanpa antrean panjang di pintu masuk.",
    path: "M4 4h6v6H4V4zm0 10h6v6H4v-6zM14 4h6v6h-6V4zm0 10h2v2h-2v-2zm4 0h2v2h-2v-2zm-4 4h2v2h-2v-2zm4 0h2v2h-2v-2z",
  },
  {
    title: "Bendahara & laporan",
    desc: "Lihat ringkasan dana masuk, status pembayaran, dan laporan peserta secara real-time dari satu dasbor.",
    path: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
  },
  {
    title: "Registrasi grup",
    desc: "Satu orang bisa mendaftarkan banyak peserta sekaligus — cocok untuk keluarga, sel, atau rombongan komunitas.",
    path: "M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a3 3 0 10-2.83-4",
  },
];

const plans = [
  {
    name: "Free",
    price: "[GRATIS]",
    cadence: "untuk acara pertama Anda",
    highlight: false,
    perks: [
      "1 acara aktif",
      "Hingga 100 peserta per acara",
      "Formulir kustom & check-in QR",
    ],
  },
  {
    name: "Pro",
    price: "[Rp XXX.XXX]",
    cadence: "per acara",
    highlight: true,
    perks: [
      "Hingga 10 acara aktif",
      "Hingga 1.000 peserta per acara",
      "Semua fitur Free",
      "Laporan bendahara lengkap",
    ],
  },
  {
    name: "Enterprise",
    price: "[Hubungi kami]",
    cadence: "acara & peserta tanpa batas",
    highlight: false,
    perks: [
      "Acara tanpa batas",
      "Peserta tanpa batas",
      "Dukungan prioritas",
      "Onboarding khusus",
    ],
  },
];

onMounted(() => {
  document.title = "Convena — Platform Registrasi & Tiket Acara Anda Sendiri";
  const desc = document.querySelector('meta[name="description"]');
  if (desc)
    desc.setAttribute(
      "content",
      "Convena memberi komunitas, gereja, dan penyelenggara konferensi platform registrasi & tiket bermerek sendiri — tanpa lewat marketplace.",
    );
});
</script>

<template>
  <div class="font-jakarta bg-convena-white min-h-screen text-gray-800">
    <!-- ─── NAV ─── -->
    <nav
      class="sticky top-0 z-50 bg-[#F7FDFB]/90 backdrop-blur-sm border-b border-convena-foam px-6 py-4"
    >
      <div class="max-w-6xl mx-auto flex items-center justify-between">
        <div class="flex items-center gap-2.5">
          <svg width="32" height="32" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="52" cy="196" r="30" fill="#9FE1CB" />
            <circle cx="128" cy="128" r="38" fill="#1D9E75" />
            <circle cx="204" cy="60" r="46" fill="#0F6E56" />
            <path d="M52 196 Q128 224 204 196" stroke="#C8EFE4" stroke-width="6" fill="none" stroke-linecap="round" />
          </svg>
          <span class="font-bold text-xl text-convena-forest tracking-tight">Convena</span>
        </div>
        <div class="flex items-center gap-2 sm:gap-4">
          <a :href="loginUrl" class="text-sm font-semibold text-convena-forest hover:text-convena-deep px-3 py-2">
            Masuk
          </a>
          <a
            :href="registerUrl"
            class="px-5 py-2.5 bg-convena-teal hover:bg-convena-forest text-white text-sm font-semibold rounded-full transition-colors shadow-sm"
          >
            Mulai Sekarang
          </a>
        </div>
      </div>
    </nav>

    <!-- ─── 1. HERO ─── -->
    <section class="relative overflow-hidden bg-gradient-to-br from-convena-white via-convena-foam to-white">
      <div class="absolute top-0 right-0 w-[480px] h-[480px] rounded-full bg-convena-mint opacity-30 blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
      <div class="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-convena-jade opacity-20 blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none"></div>

      <div class="relative max-w-3xl mx-auto px-6 pt-20 pb-20 text-center">
        <div class="inline-flex items-center gap-2 bg-white text-convena-teal text-xs font-semibold px-4 py-1.5 rounded-full mb-6 border border-convena-foam shadow-sm uppercase tracking-widest">
          <span class="w-1.5 h-1.5 rounded-full bg-convena-teal animate-pulse"></span>
          Platform Acara untuk Komunitas
        </div>

        <h1 class="text-4xl md:text-6xl font-extrabold text-convena-deep leading-[1.1] tracking-tight mb-6">
          Platform registrasi acara<br />
          <span class="text-convena-teal">milik Anda sendiri.</span>
        </h1>

        <p class="text-base md:text-lg text-gray-500 leading-relaxed mb-10 max-w-xl mx-auto">
          Untuk komunitas, gereja, dan penyelenggara konferensi di Indonesia —
          kelola pendaftaran, pembayaran, dan tiket acara Anda tanpa bergantung
          pada marketplace atau spreadsheet manual.
        </p>

        <div class="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            :href="registerUrl"
            class="px-8 py-3.5 bg-convena-teal hover:bg-convena-forest text-white font-semibold rounded-full transition-colors text-base shadow-lg"
          >
            Mulai Gratis
          </a>
          <a
            href="#fitur"
            class="px-8 py-3.5 bg-white hover:bg-convena-foam text-convena-forest font-semibold rounded-full transition-colors text-base border border-convena-foam"
          >
            Lihat Cara Kerja
          </a>
        </div>
      </div>
    </section>

    <!-- ─── 2. PROBLEM / SOLUTION ─── -->
    <section class="px-6 py-20 bg-white">
      <div class="max-w-4xl mx-auto text-center">
        <h2 class="text-2xl md:text-3xl font-bold text-convena-deep mb-4">
          Berhenti menyulap spreadsheet dan grup WhatsApp.
        </h2>
        <p class="text-gray-500 leading-relaxed max-w-2xl mx-auto mb-12">
          Mengandalkan platform marketplace berarti komisi besar dan merek
          mereka, bukan Anda. Mengelola manual berarti data peserta tersebar di
          spreadsheet, konfirmasi transfer dicek satu per satu, dan koordinasi
          berantakan di banyak chat. Convena menyatukan semuanya.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-5 text-left">
          <div class="bg-convena-foam/50 rounded-2xl p-6">
            <p class="text-convena-forest font-semibold mb-1">Merek Anda sendiri</p>
            <p class="text-sm text-gray-500">Halaman pendaftaran atas nama organisasi Anda, bukan marketplace.</p>
          </div>
          <div class="bg-convena-foam/50 rounded-2xl p-6">
            <p class="text-convena-forest font-semibold mb-1">Semua di satu tempat</p>
            <p class="text-sm text-gray-500">Pendaftaran, pembayaran, tiket, dan laporan dalam satu dasbor.</p>
          </div>
          <div class="bg-convena-foam/50 rounded-2xl p-6">
            <p class="text-convena-forest font-semibold mb-1">Tanpa komisi marketplace</p>
            <p class="text-sm text-gray-500">Bayar per acara dengan tarif jelas — bukan potongan per tiket.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── 3. FEATURES ─── -->
    <section id="fitur" class="px-6 py-20 bg-convena-white scroll-mt-20">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-bold text-convena-deep mb-3">
            Semua yang Anda butuhkan untuk mengelola acara
          </h2>
          <p class="text-gray-500 max-w-xl mx-auto">
            Dirancang untuk cara komunitas Indonesia benar-benar bekerja.
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <div
            v-for="f in features"
            :key="f.title"
            class="bg-white rounded-2xl p-6 border border-convena-foam hover:shadow-md transition-shadow"
          >
            <div class="w-11 h-11 rounded-xl bg-convena-foam flex items-center justify-center mb-4">
              <svg class="w-5 h-5 text-convena-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" :d="f.path" />
              </svg>
            </div>
            <h3 class="font-bold text-convena-deep mb-1.5">{{ f.title }}</h3>
            <p class="text-sm text-gray-500 leading-relaxed">{{ f.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── 4. PRICING ─── -->
    <section id="harga" class="px-6 py-20 bg-white scroll-mt-20">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-4">
          <h2 class="text-2xl md:text-3xl font-bold text-convena-deep mb-3">
            Harga sederhana, per acara
          </h2>
          <p class="text-gray-500 max-w-xl mx-auto">
            Bayar untuk acara yang Anda jalankan — bukan langganan bulanan, bukan
            komisi per tiket. Mulai gratis untuk acara pertama Anda.
          </p>
        </div>
        <!-- Placeholder-pricing note for the operator -->
        <p class="text-center text-xs text-amber-600 mb-10">
          [Harga di bawah adalah placeholder — operator mengisi tarif final per
          acara, termasuk tarif acara lanjutan.]
        </p>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div
            v-for="p in plans"
            :key="p.name"
            class="rounded-2xl p-6 bg-white flex flex-col"
            :class="
              p.highlight
                ? 'border-2 border-convena-teal shadow-lg relative'
                : 'border border-convena-foam'
            "
          >
            <div
              v-if="p.highlight"
              class="absolute -top-3 left-1/2 -translate-x-1/2 bg-convena-teal text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide"
            >
              Paling populer
            </div>
            <h3 class="font-bold text-lg text-convena-deep">{{ p.name }}</h3>
            <div class="mt-3 mb-1">
              <span class="text-2xl font-extrabold text-convena-forest">{{ p.price }}</span>
            </div>
            <p class="text-xs text-gray-400 mb-5">{{ p.cadence }}</p>
            <ul class="space-y-2.5 text-sm mb-6 flex-1">
              <li v-for="perk in p.perks" :key="perk" class="flex items-start gap-2">
                <svg class="w-4 h-4 text-convena-jade mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span class="text-gray-600">{{ perk }}</span>
              </li>
            </ul>
            <a
              :href="registerUrl"
              class="text-center px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
              :class="
                p.highlight
                  ? 'bg-convena-teal hover:bg-convena-forest text-white'
                  : 'bg-convena-foam hover:bg-convena-mint text-convena-forest'
              "
            >
              Mulai Sekarang
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── 5. FINAL CTA ─── -->
    <section class="px-6 py-20 bg-gradient-to-br from-convena-forest to-convena-deep">
      <div class="max-w-3xl mx-auto text-center">
        <h2 class="text-2xl md:text-4xl font-bold text-white mb-4">
          Siap menjalankan acara Anda dengan lebih rapi?
        </h2>
        <p class="text-convena-mint/90 mb-8 max-w-xl mx-auto">
          Buat akun gratis dan siapkan acara pertama Anda dalam hitungan menit.
        </p>
        <a
          :href="registerUrl"
          class="inline-block px-8 py-3.5 bg-white hover:bg-convena-foam text-convena-forest font-semibold rounded-full transition-colors text-base shadow-lg"
        >
          Mulai Gratis Sekarang
        </a>
      </div>
    </section>

    <!-- ─── 6. FOOTER ─── -->
    <footer class="bg-convena-deep border-t border-white/10 px-6 py-8">
      <div class="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div class="flex items-center gap-2.5">
          <svg width="22" height="22" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="52" cy="196" r="30" fill="#9FE1CB" />
            <circle cx="128" cy="128" r="38" fill="#1D9E75" />
            <circle cx="204" cy="60" r="46" fill="#5DCAA5" />
            <path d="M52 196 Q128 224 204 196" stroke="#C8EFE4" stroke-width="6" fill="none" stroke-linecap="round" />
          </svg>
          <span class="text-white font-bold text-base tracking-tight">Convena</span>
        </div>

        <div class="flex flex-col md:items-end items-center gap-2">
          <nav class="flex items-center gap-4 text-xs flex-wrap justify-center">
            <RouterLink to="/terms" class="text-white/50 hover:text-white/80">Ketentuan Layanan</RouterLink>
            <RouterLink to="/privacy" class="text-white/50 hover:text-white/80">Kebijakan Privasi</RouterLink>
            <a href="mailto:[CONTACT EMAIL]" class="text-white/50 hover:text-white/80">Kontak</a>
          </nav>
          <p class="text-white/30 text-xs text-center md:text-right">
            © {{ new Date().getFullYear() }} Convena. Platform registrasi acara komunitas.
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped></style>
